import { type AxiosResponse } from "axios";
import { type IntlShape } from "react-intl";

import { packNetAxios } from "@/lib/api-client";
import {
	type EmMachine,
	type TrackOffsetRequest,
	type TrackOffsetValidationResult,
	getTrackOffsetRequestSchema,
	trackOffsetValidationResultSchema,
} from "@/schemas";

import { baseRoutes } from "../base-routes";

const emApi = `${baseRoutes.em}/api/v1/machines`;

export async function getEmMachines(): Promise<EmMachine[]> {
	const response = await packNetAxios.get<EmMachine[]>(emApi);

	return response.data.map((machine) => ({ ...machine, machineType: "Em" as const }));
	// return z.array(emMachineSchema).parse(response.data); // TODO: Test parse
}

export async function deleteEmMachine(machineId: string): Promise<void> {
	await packNetAxios.delete(`${emApi}/${machineId}`);
}

// Track offset operations
export async function releaseBrakes(machineId: string, trackNumber: number): Promise<AxiosResponse> {
	return packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/changecorrugate`);
}

export async function produceTrackOffset(machineId: string, trackNumber: number): Promise<AxiosResponse> {
	return packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/offset`);
}

export async function setTrackOffset(
	machineId: string,
	trackNumber: number,
	data: TrackOffsetRequest,
): Promise<AxiosResponse> {
	return packNetAxios.put(`${emApi}/${machineId}/tracks/${trackNumber}/offset`, data);
}

export async function validateTrackOffset(
	machineId: string,
	trackNumber: number,
	data: TrackOffsetRequest,
	intl: IntlShape,
): Promise<TrackOffsetValidationResult> {
	const request = getTrackOffsetRequestSchema(intl).parse(data);
	const response = await packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/offset/validate`, request);

	return trackOffsetValidationResultSchema.parse(response.data);
}

// Service mode operations
export async function enterEmServiceMode(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/enterservicemode`, {});
}

export async function leaveEmServiceMode(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/leaveservicemode`, {});
}

// LongHead operations
export async function activateCreaseWheel(machineId: string, longheadNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/longheads/${longheadNumber}/activatecreasewheel`, {});
}

export async function raiseCreaseWheel(machineId: string, longheadNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/longheads/${longheadNumber}/raisecreasewheel`, {});
}

export async function activateLatch(machineId: string, longheadNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/longheads/${longheadNumber}/activatelatch`, {});
}

export async function deactivateLatch(machineId: string, longheadNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/longheads/${longheadNumber}/deactivatelatch`, {});
}

export async function activateKnife(machineId: string, longheadNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/longheads/${longheadNumber}/activateknife`, {});
}

export async function raiseKnife(machineId: string, longheadNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/longheads/${longheadNumber}/raiseknife`, {});
}

// Track operations
export async function activateBrakes(machineId: string, trackNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/activatebrakes`, {});
}

export async function deactivateBrakes(machineId: string, trackNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/deactivatebrakes`, {});
}

export async function activatePressureRoller(machineId: string, trackNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/activatepressureroller`, {});
}

export async function deactivatePressureRoller(machineId: string, trackNumber: number): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/tracks/${trackNumber}/deactivatepressureroller`, {});
}

// CrossHead operations
export async function activateCrossheadCreaseWheel(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/activatecrossheadcreasewheel`, {});
}

export async function raiseCrossheadCreaseWheel(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/raisecrossheadcreasewheel`, {});
}

export async function activateCrossheadLatch(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/activatecrossheadlatch`, {});
}

export async function deactivateCrossheadLatch(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/deactivatecrossheadlatch`, {});
}

export async function activateCrossheadKnife(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/activatecrossheadknife`, {});
}

export async function raiseCrossheadKnife(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/raisecrossheadknife`, {});
}

// Cycle operations
export async function cycleAllCrossheadTools(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/cycleallcrossheadtools`, {});
}

export async function cycleAllLongheadTools(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/cyclealllongheadtools`, {});
}

export async function cycleAllTrackTools(machineId: string): Promise<void> {
	await packNetAxios.post(`${emApi}/${machineId}/cyclealltracktools`, {});
}

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.em}/api/v1/version`);
	return response.data;
}
